import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import React from "react";
import { ApiError } from "../../Api/ApiError";
import { editCustomerData, getCustomerDetail, registerCustomer } from "../../Api/Customer";
import { Addr, Zipcode } from "../../Api/Zipcode";
import { CustomerDetailInterface } from "../../Objects/Customer";
import CustomerCard from "../molecules/CustomerCard";
import ListDialog from "../molecules/ListDialog";

export interface CustomerDataEditDialogProps {
    ctmId?: number
    dialogProps?: Omit<DialogProps, "open">
    onClose: () => void
    onUpdate?: (ctm_id: number) => void
}
export function CustomerDataEditDialog({
    ctmId,
    dialogProps,
    onClose,
    onUpdate,
}: CustomerDataEditDialogProps): React.ReactElement {
    let { enqueueSnackbar } = useSnackbar()

    const [open, setOpen] = React.useState<boolean>(false)

    const [customerDetailData, setCustomerDetailData] = React.useState<CustomerDetailInterface>()

    const [editName1, setEditName1] = React.useState<string>("")
    const [editName2, setEditName2] = React.useState<string>("")
    const [editPronunciation11, setEditPronunciation11] = React.useState<string>("")
    const [editPronunciation12, setEditPronunciation12] = React.useState<string>("")
    const [editBirthday, setEditBirthday] = React.useState<Date | null>(null)
    const [editGender, setEditGender] = React.useState<string | null>(null)
    const [editPostcode, setEditPostcode] = React.useState<string>("")
    const [editAddress1, setEditAddress1] = React.useState<string>("")
    const [editAddress2, setEditAddress2] = React.useState<string>("")
    const [editTel1, setEditTel1] = React.useState<string>("")
    const [editTel2, setEditTel2] = React.useState<string>("")
    const [editMail1, setEditMail1] = React.useState<string>("")
    const [editMail2, setEditMail2] = React.useState<string>("")

    const abortController = React.useMemo(() => new AbortController(), [])

    const [openAddrSelect, setOpenAddrSelect] = React.useState<boolean>(false)
    const [addrSelectList, setAddrSelectList] = React.useState<Addr[]>([])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const doZipcodeToAddress = React.useCallback(() => {
        if (editPostcode) {
            Zipcode({
                code: editPostcode
            }, { signal: abortController.signal }).then(result => {
                if (result.addr.length < 1) {
                    enqueueSnackbar("住所が見つかりませんでした", { variant: "warning" })
                } else if (result.addr.length > 1) {
                    setAddrSelectList(result.addr)
                    setOpenAddrSelect(true)
                } else {
                    setEditAddress1(`${result.addr[0].pref}${result.addr[0].city}${result.addr[0].town}`)
                }
            }).catch(e => {
                if (e.__CANCEL__) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [editPostcode, enqueueSnackbar, abortController])

    const doSave = React.useCallback(() => {
        if (ctmId) {
            //validate
            // @todo
            editCustomerData({
                ctm_id: ctmId,
                ctm_name1: editName1,
                ctm_name2: editName2,
                ctm_pronunciation1: editPronunciation11,
                ctm_pronunciation2: editPronunciation12,
                ctm_address1: editAddress1,
                ctm_address2: editAddress2,
                // ctm_address3,
                ctm_birthday: editBirthday ? editBirthday : undefined,
                ctm_gender: editGender ? editGender : undefined,
                ctm_mail1: editMail1,
                ctm_mail2: editMail2,
                ctm_postcode: editPostcode,
                ctm_tel1: editTel1,
                ctm_tel2: editTel2,
            }, { signal: abortController.signal }).then(() => {
                enqueueSnackbar("登録しました", { variant: "success" })
                setOpen(false)
                if (onUpdate) {
                    onUpdate(ctmId)
                }
            }).catch((e: any) => {
                if (e.__CANCEL__) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        } else if (ctmId === 0) {
            registerCustomer({
                ctm_name1: editName1,
                ctm_name2: editName2,
                ctm_pronunciation1: editPronunciation11,
                ctm_pronunciation2: editPronunciation12,
                ctm_address1: editAddress1,
                ctm_address2: editAddress2,
                // ctm_address3,
                ctm_birthday: editBirthday ? editBirthday : undefined,
                ctm_gender: editGender ? editGender : undefined,
                ctm_mail1: editMail1,
                ctm_mail2: editMail2,
                ctm_postcode: editPostcode,
                ctm_tel1: editTel1,
                ctm_tel2: editTel2,
            }, { signal: abortController.signal }).then((result) => {
                enqueueSnackbar("登録しました", { variant: "success" })
                setOpen(false)
                if (onUpdate) {
                    onUpdate(result.ctm_id)
                }
            }).catch((e: any) => {
                if (e.__CANCEL__) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, onUpdate, ctmId, editName1, editName2, editPronunciation11, editPronunciation12, editBirthday, editGender, editPostcode, editAddress1, editAddress2, editTel1, editTel2, editMail1, editMail2, enqueueSnackbar])

    React.useEffect(() => {
        const controller = new AbortController()
        if (ctmId) {
            getCustomerDetail({
                accessKey: "HEAD-OFFICE_LOGIN",
                ctmId: ctmId
            }, { signal: controller.signal }).then(result => {
                setCustomerDetailData(result)
                setEditPronunciation11(result.ctm_pronunciation1 ? result.ctm_pronunciation1 : "")
                setEditPronunciation12(result.ctm_pronunciation2 ? result.ctm_pronunciation2 : "")
                setEditName1(result.ctm_name1 ? result.ctm_name1 : "")
                setEditName2(result.ctm_name2 ? result.ctm_name2 : "")
                setEditAddress1(result.ctm_address1 ? result.ctm_address1 : "")
                setEditAddress2(result.ctm_address2 ? result.ctm_address2 : "")
                setEditTel1(result.ctm_tel1 ? result.ctm_tel1 : "")
                setEditTel2(result.ctm_tel2 ? result.ctm_tel2 : "")
                setEditMail1(result.ctm_mail1 ? result.ctm_mail1 : "")
                setEditMail2(result.ctm_mail2 ? result.ctm_mail2 : "")
                setEditBirthday(result.ctm_birthday ? new Date(result.ctm_birthday) : null)
                setEditGender(result.ctm_gender ? result.ctm_gender : "")
                setEditPostcode(result.ctm_postcode ? result.ctm_postcode : "")
                setOpen(true)
            }).catch((e: any) => {
                if (e.__CANCEL__) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        } else if (ctmId === 0) {
            setOpen(true)
        }
        return () => {
            controller.abort()
        }
    }, [ctmId, enqueueSnackbar])

    return (
        <Dialog
            {...dialogProps}
            open={open}
            onClose={() => {
                setOpen(false)
            }}
            TransitionProps={{
                onExited: () => {
                    onClose()
                }
            }}
        >
            <DialogTitle>{customerDetailData ? "顧客データ編集" : "新規顧客登録"}</DialogTitle>
            <DialogContent>
                {customerDetailData &&
                    <CustomerCard customer={customerDetailData} />
                }
                <Grid container
                    spacing={1}
                    rowSpacing={2}
                    mt={1}
                    alignItems={"center"}
                >
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="よみがな（せい）"
                            value={editPronunciation11}
                            onChange={e => {
                                setEditPronunciation11(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="よみがな（めい）"
                            value={editPronunciation12}
                            onChange={e => {
                                setEditPronunciation12(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="名前（姓）"
                            value={editName1}
                            onChange={e => {
                                setEditName1(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="名前（名）"
                            value={editName2}
                            onChange={e => {
                                setEditName2(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DatePicker
                            label="生年月日"
                            onChange={date => {
                                setEditBirthday(date)
                            }}
                            value={editBirthday}
                            renderInput={params => <TextField {...params} fullWidth size="small" />}
                            inputFormat="yyyy/MM/dd"
                            mask="____/__/__"
                            clearable={true}
                            defaultCalendarMonth={null}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="性別"
                            value={editGender ?? ""}
                            onChange={e => {
                                setEditGender(e.target.value)
                            }}
                            fullWidth
                            select
                        >
                            <MenuItem value="male">男性</MenuItem>
                            <MenuItem value="female">女性</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={9} sm={6}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="郵便番号"
                            value={editPostcode}
                            onChange={e => {
                                setEditPostcode(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={3} sm={3}>
                        <Button
                            variant="outlined"
                            onClick={doZipcodeToAddress}
                            fullWidth
                        >住所検索</Button>
                        <ListDialog
                            open={openAddrSelect}
                            values={addrSelectList.map(addr => `${addr.pref}${addr.city}${addr.town}`)}
                            onClose={(selected) => {
                                setOpenAddrSelect(false)
                                setEditAddress1(selected)
                            }}
                            onCancel={() => {
                                setOpenAddrSelect(false)
                            }}
                            title="住所を選択してください"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="住所 (県・市区町村)"
                            value={editAddress1}
                            onChange={e => {
                                setEditAddress1(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="住所（番地・マンション・アパート名）"
                            value={editAddress2}
                            onChange={e => {
                                setEditAddress2(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="電話番号（携帯）"
                            value={editTel1}
                            onChange={e => {
                                setEditTel1(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="電話番号（その他）"
                            value={editTel2}
                            onChange={e => {
                                setEditTel2(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="メール（携帯）"
                            value={editMail1}
                            onChange={e => {
                                setEditMail1(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="メール（PC）"
                            value={editMail2}
                            onChange={e => {
                                setEditMail2(e.target.value)
                            }}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setOpen(false)
                    }}
                >キャンセル</Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        doSave()
                    }}
                >保存</Button>
            </DialogActions>
        </Dialog>
    )
}